/**
 * List page state views — one per failure / empty mode. Shared by
 * Submission Triage and Policies, which the design gives the same three
 * screens (same copy, same art).
 *
 * All three views fill the available card body height and keep their
 * content vertically + horizontally centered. The parent slot gives
 * them flex-1 min-h-0 so the page chrome (toolbar + pagination) stays
 * pinned while the centered illustration sits in the dead space.
 */
import { AlertCircle, RefreshCw05 } from "@untitledui/icons";
import { Button } from "@tricura/atlas-ui";
import folderIcon from "../assets/folder-icon.svg";
// Exported from the Policies design (Andressa, OR-1537): cloud + search art.
// `?inline` bakes it into the bundle as a data URI: this screen shows
// exactly when the network is gone, so it can't fetch its art.
import offlineIllustration from "../assets/offline-illustration.svg?inline";

const STATE_SHELL = "flex flex-1 min-h-0 flex-col items-center justify-center gap-4 px-6 text-center";

interface ErrorStateProps {
    onRetry: () => void;
}

export function ListErrorState({ onRetry }: ErrorStateProps) {
    return (
        <div className={STATE_SHELL}>
            <div
                className="size-12 flex items-center justify-center rounded-full"
                style={{ backgroundColor: "var(--color-bg-error-secondary)" }}
            >
                <AlertCircle aria-hidden="true" className="text-fg-error-secondary size-6" />
            </div>
            <div className="flex flex-col gap-1">
                <h2 className="text-primary text-lg font-semibold">Something went wrong...</h2>
                {/* Line break placed as in the design, not left to the wrap. */}
                <p className="text-tertiary text-sm">
                    We had some trouble loading this page. Please
                    <br />
                    refresh the page.
                </p>
            </div>
            <Button color="primary" size="md" onClick={onRetry}>
                Try again
            </Button>
        </div>
    );
}

interface OfflineStateProps {
    onRefresh: () => void;
}

export function ListOfflineState({ onRefresh }: OfflineStateProps) {
    return (
        <div className={STATE_SHELL}>
            <img src={offlineIllustration} alt="" aria-hidden="true" width={154} height={121} />
            <div className="flex flex-col gap-1">
                <h2 className="text-primary text-md font-semibold">You're offline</h2>
                <p className="text-tertiary text-sm">
                    Please check your internet connection and try again.
                    <br />
                    If the issue continues,{" "}
                    <a href="mailto:support@orionunderwriting.io" className="text-tertiary hover:text-secondary underline underline-offset-2">
                        contact support
                    </a>
                    .
                </p>
            </div>
            <Button color="secondary" size="md" iconLeading={RefreshCw05} onClick={onRefresh}>
                Refresh page
            </Button>
        </div>
    );
}

/** "No Results Found". The subtitle varies by screen: lists with filters
 *  mention them, the policy record's tabs only have a search box. */
export function ListEmptyState({ description = "Check your filters or try a different search" }: { description?: string }) {
    return (
        <div className={STATE_SHELL}>
            <img src={folderIcon} alt="" aria-hidden="true" className="size-12" />
            <div className="flex flex-col gap-1">
                <h2 className="text-primary text-md font-semibold">No Results Found</h2>
                <p className="text-tertiary text-sm">{description}</p>
            </div>
        </div>
    );
}
